import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import 'rxjs/add/operator/toPromise';
import { environment } from 'src/environments/environment';
import { ErrorHandlerService } from 'src/app/shared/services/error-handler.service';
import { BehaviorSubject, Subject } from 'rxjs';
import { CurrentUserService } from 'src/app/core/services/current-user.service';
import { RequestAccessTypesEnum } from 'src/app/shared/enum/request-access-types.enum';


@Injectable()
export class CustomerService {

  public apiUrl = environment.runtime.userManagementApiUrl;
  public userUrl = environment.runtime.userManagementApiUrl;
  public customersUrl = environment.runtime.userManagementApiUrl;
  public all: {}[] = [];
  public newCustomerUser: any = {};
  public updatedCustomerUser: any = { organization: {} };
  public newCustomerUserRequest: any = {};
  public nameIsInvalid: boolean = false;
  public invalidNameErrorMessage: string = '';
  public filter: string = '';
  public requestsCount: number = 0;
  public currentCustomerID: string = '';
  public currentCustomerID_ = new BehaviorSubject(String);
  public currentCustomerID$ = this.currentCustomerID_.asObservable();

  public requestCount = new Subject<object>();
  public requestCount$ = this.requestCount.asObservable();

  constructor(private authHttp: HttpClient, private errorHandlerService: ErrorHandlerService,
    private currentUserService: CurrentUserService) { }

  getCustomers(): Promise<{}[]> {
    return this.authHttp.get(`${this.apiUrl}/customers`)
      .toPromise()
      .then((response: any) => {
        this.filterCustomers(response.customers);
        return response.customers;
      })
      .catch(e => this.errorHandlerService.handleError(e, e.message));
  }

  private filterCustomers(customers) {
    let sortedUsers: {}[];
    sortedUsers = customers.sort((a: any, b: any) => {
      if (!a.name)
        a.name = '';
      let x = a['name'].toLowerCase();
      if (!b.name)
        b.name = '';
      let y = b['name'].toLowerCase();
      if (x < y) {
        return -1;
      } else if (x > y) {
        return 1;
      }
      return 0;
    });
    this.all = customers.sort(customer => customer.name);
  }

  public changeCustomerId(id) {
    this.currentCustomerID = id;
    this.currentCustomerID_.next(id);
    this.newCustomerUser.allowed_origin_locations = [];
    this.newCustomerUser.allowed_destination_locations = [];
  }

  public changeCustomer(customer) {
    this.updatedCustomerUser.organization['organizationName'] = customer.name;
    this.updatedCustomerUser.organization['organizationCode'] = customer.id;
    this.updatedCustomerUser.allowed_origin_locations = [];
    this.updatedCustomerUser.allowed_destination_locations = [];
    delete this.updatedCustomerUser.allowed_destination_locations_display;
    delete this.updatedCustomerUser.allowed_origin_locations_display;
    this.newCustomerUser.allowed_origin_locations = [];
    this.newCustomerUser.allowed_destination_locations = [];
  }

  public invalidName(event, name) {
    let invalidPattern = event.srcElement.validity.patternMismatch;
    if (!invalidPattern) {
      this.nameIsInvalid = false;
    } else if (invalidPattern && name.includes('  ')) {
      this.nameIsInvalid = true;
      this.invalidNameErrorMessage = "Please enter both a first and last name seperated by single space characters";
    } else if (invalidPattern && !name.includes('  ')) {
      this.nameIsInvalid = true;
      this.invalidNameErrorMessage = "Please enter both a first and last name";
    }
  }

  getCustomer(id: string): {} {
    return this.all.filter(customer => customer['id'] === id)[0];
  }

  getCustomerUser(id: string): Promise<{}[]> {
    return this.authHttp.get(`${this.userUrl}/user/get/` + id)
      .toPromise()
      .then(response => response)
      .catch(e => this.errorHandlerService.handleError(e, e.message));
  }

  createCustomerUser(): Promise<any> {
    return this.authHttp.post(`${this.userUrl}/user/create`, this.newCustomerUser).toPromise();
  }

  updateCustomerUser(id: string): Promise<{}[]> {
    delete this.updatedCustomerUser.allowed_destination_locations_display;
    delete this.updatedCustomerUser.allowed_origin_locations_display;
    return this.authHttp.post(`${this.userUrl}/user/update/other/` + id, this.updatedCustomerUser)
      .toPromise()
      .then(response => response)
      .catch(e => this.errorHandlerService.handleError(e, e.message));
  }

  resetCustomerUser(): void {
    this.newCustomerUser = {};
  }

  resetUpdateUser(): void {
    this.updatedCustomerUser = { organization: {} };
  }

  getCustomerUsersAndCounts(id: string): Promise<{}[]> {
    return this.authHttp.get(`${this.customersUrl}/customers/` + id)
      .toPromise()
      .then(response => response)
      .catch(e => this.errorHandlerService.handleError(e, e.message));
  }

  createCustomerRequest(): Promise<{}[]> {
    const isCordovaApp = document.URL.indexOf('http://') === -1
    let url = `${this.apiUrl}/customer/customer_requests`;
    if (isCordovaApp) {
      url = window.location.origin;
      url = url + '/hubconnectusermanagementapi/customer/customer_requests'
    }
    return this.authHttp.post(url, this.newCustomerUserRequest)
      .toPromise()
      .then(response => response)
      .catch(e => this.errorHandlerService.handleError(e, e.message))
  }

  getCustomerRequests(requestType: string = RequestAccessTypesEnum.CUSTOMER): Promise<{}[]> {
    return this.authHttp.get(`${this.customersUrl}/customer/customer_requests?requestType=${requestType}`)
      .toPromise()
      .then(response => response)
      .catch(e => this.errorHandlerService.handleError(e, e.message))
  }

  getCustomerRequestsCount() {
    let custCount = null;
    let supCount = null;
    let carrierCount = null;

    if (this.checkPrivilege('HC_CUSTOMER_CREATE_USER')) {
      custCount = this.authHttp.get(`${this.customersUrl}/customer/customer_requests/count?requestType=${RequestAccessTypesEnum.CUSTOMER}`).toPromise();
    }
    if (this.checkPrivilege('HC_SUPPLIER_CREATE_USER')) {
      supCount = this.authHttp.get(`${this.customersUrl}/customer/customer_requests/count?requestType=${RequestAccessTypesEnum.SUPPLIER}`).toPromise();
    }
    if (this.checkPrivilege('HC_CR_CREATE_USER')) {
      carrierCount = this.authHttp.get(`${this.customersUrl}/carriers/carrier_requests/count?requestType=${RequestAccessTypesEnum.CARRIER}`).toPromise();
    }
    Promise.all([custCount, supCount, carrierCount]).then(reqCounts => {
      this.requestCount.next(reqCounts[0] + reqCounts[1] + reqCounts[2]);
    }).catch();
  }

  private checkPrivilege(entitlement) {
    return this.currentUserService.user.role.entitlements.find(val => {
      if (val.code === entitlement)
        return true;
    });
  }

  getCustomerRequest(id: string, requestType: string): Promise<{}[]> {
    return this.authHttp.get(`${this.customersUrl}/customer/customer_requests/${id}?requestType=${requestType}`)
      .toPromise()
      .then(response => response)
      .catch(e => this.errorHandlerService.handleError(e, e.message))
  }

  getCustomerSupplierReleaseRequirements(customerId: string): Promise<any> {
    return this.authHttp.get(`${this.customersUrl}/customer/supplier_release_requirements/${customerId}`)
      .toPromise()
      .then(response => response)
      .catch(e => {
        this.errorHandlerService.handleError(e, e.message)
        throw e;
      });
  }

  updateCustomerSupplierReleaseRequirements(supplierReleaseRequirements: object, customerId: string): Promise<{}[]> {
    return this.authHttp.put(`${this.customersUrl}/customer/supplier_release_requirements/${customerId}`, supplierReleaseRequirements)
      .toPromise()
      .then(response => response)
      .catch(e => this.errorHandlerService.handleError(e, e.message))
  }

  removeCustomerRequest(id: string = this.newCustomerUser.requestId, requestType: string = this.newCustomerUser.requestType): Promise<{}[]> {
    return this.authHttp.delete(`${this.customersUrl}/customer/customer_requests/${id}?requestType=${requestType}`)
      .toPromise()
      .then(response => {
        this.getCustomerRequestsCount();
        return response;
      })
      .catch(e => this.errorHandlerService.handleError(e, e.message))
  }

  updateCustomerRequest(id, followUp, comments, requestType): Promise<{}[]> {
    return this.authHttp.put(`${this.customersUrl}/customer/customer_requests`, { requestId: id, followUp: followUp, comments: comments, requestType: requestType })
      .toPromise()
      .then(response => response)
      .catch(e => this.errorHandlerService.handleError(e, e.message))
  }

  getRole(roleCode) {
    return this.authHttp.get(`${this.apiUrl}/auth/role/${roleCode}`)
      .toPromise()
      .then(response => response)
      .catch(e => this.errorHandlerService.handleError(e, e.message))
  }

  getOrganizationRoles(customerid) {
    return this.authHttp.get(`${this.apiUrl}/organization/role/${customerid}`)
      .toPromise()
      .then(response => response)
      .catch(e => { throw e})
  }

  createOrganizationRole(role, orgId, status = true) {
    let type = 'CUSTOMER';
    let id = orgId;
    let enabled = status;
    return this.authHttp.post(`${this.apiUrl}/organization/role`, {
      "id": id,
      "type": type,
      "status": enabled,
      "role": role
    })
      .toPromise()
      .then(response => response)
      .catch(e => this.errorHandlerService.handleError(e, e.message))
  }

  updateOrganizationRoles(id, status) {
    this.authHttp.put(`${this.apiUrl}/organization/role`, {
      "id": id,
      "status": status
    })
      .toPromise()
      .then(response => response)
      .catch(e => this.errorHandlerService.handleError(e, e.message))
  }

  updateOrganizationEntitlements(id, entitlements) {
    this.authHttp.put(`${this.apiUrl}/organization/entitlements`, {
      "id": id,
      "entitlements": entitlements
    })
      .toPromise()
      .then(response => response)
      .catch(e => this.errorHandlerService.handleError(e, e.message))
  }
  /**
   * api calls for Carrier requests and Factoring Company requests.
   */
  createCarrierRequest(): Promise<{}[]> {
    const isCordovaApp = document.URL.indexOf('http://') === -1
    let url = `${this.apiUrl}/carriers/carrier_request`;
    if (isCordovaApp) {
      url = window.location.origin;
      url = url + '/hubconnectusermanagementapi/carriers/carrier_request'
    }
    return this.authHttp.post(url, this.newCustomerUserRequest)
      .toPromise()
      .then(response => response)
      .catch(e => this.errorHandlerService.handleError(e, e.message))
  }

  getCarrierRequests(requestType: string): Promise<{}[]> {
    return this.authHttp.get(`${this.apiUrl}/carriers/get_requests?requestType=${requestType}`)
      .toPromise()
      .then(response => response)
      .catch(e => this.errorHandlerService.handleError(e, e.message))
  }

  getCarrierRequest(id: string, requestType: string): Promise<{}[]> {
    return this.authHttp.get(`${this.customersUrl}/carriers/get_request/${id}?requestType=${requestType}`)
      .toPromise()
      .then(response => response)
      .catch(e => this.errorHandlerService.handleError(e, e.message))
  }

  removeCarrierRequest(id: string = this.newCustomerUser.requestId, requestType: string = this.newCustomerUser.requestType): Promise<{}[]> {
    return this.authHttp.delete(`${this.customersUrl}/carriers/delete_request/${id}?requestType=${requestType}`)
      .toPromise()
      .then(response => {
        this.getCustomerRequestsCount();
        return response;
      })
      .catch(e => this.errorHandlerService.handleError(e, e.message))
  }

  updateCarriersModeAndUnit(id: string, transportMode: string = 'TL', businessUnit: string[] = ['HWY']) {
    return this.authHttp.post(`${this.customersUrl}/carriers/carrier_update_mode_unit/${id}`, { transportMode, businessUnit })
      .toPromise()
      .then(response => response)
      .catch(e => this.errorHandlerService.handleError(e, e.message));
  }

}
